import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import { FaUser } from "react-icons/fa";
import { HiChevronRight } from "react-icons/hi2";
import { HiOutlineComputerDesktop } from "react-icons/hi2";
import { IoMdReturnRight } from "react-icons/io";
import { IoMoon, IoSunny } from "react-icons/io5";
import { RiBuilding2Line, RiGiftLine, RiMapPin2Line, RiUserAddLine } from "react-icons/ri";
import { PackageContents } from "@/components/billing/cart/package-contents";
import PatientDisplay from "@/components/patient/patient-display";
import { ProgressBarLink } from "@/components/progress-bar";
import AIBadge from "@/components/ui/ai-badge";
import { CommandAction } from "@/config/command-actions";
import { SiteMapItem, getRelativePath } from "@/config/sitemap";
import { ThemeMode } from "@/contexts/theme-provider";
import { SearchResultType } from "@/lib/search-analytics";
import { cx } from "@/lib/tailwind-utils";
import { ItemType } from "@/types/enums/item-type";
import { LocationDetail } from "@/types/location";
import { PatientSummary } from "@/types/patient";
import { TenantSelect } from "@/types/tenant";
import { SEARCH_MIN_LENGTH } from "./common";
import ItemDisplay, { type CatalogSearchItem } from "./item-display";
import { GiftCardCodeSearchResult } from "@/api/provider/billing/gift-card";
const STYLES = {
  container: "flex cursor-pointer items-center rounded-lg px-2 py-1.5 text-body transition-colors focus-visible:!outline-2 focus-visible:!outline-ring hover:bg-hover",
  selected: "bg-primary/20",
  icon: "mr-2 h-4 w-4 opacity-50",
  heading: "px-2 py-1.5 text-caption-emphasis text-foreground"
} as const;
export function PageSearchResult(t0) {
  const $ = _c(19);
  const {
    page,
    onSelect,
    isSelected
  } = t0;
  const t1 = page.route ?? "#";
  let t2;
  if ($[0] !== onSelect) {
    t2 = e => {
      e.preventDefault();
      onSelect();
    };
    $[0] = onSelect;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = isSelected && STYLES.selected;
  let t4;
  if ($[2] !== t3) {
    t4 = cx(STYLES.container, t3);
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <IoMdReturnRight className={cx(STYLES.icon, "")} />;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== page.route) {
    t6 = getRelativePath(page.route ?? "").map(_temp);
    $[5] = page.route;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t6) {
    t7 = <div className="mr-4 flex flex-row items-center gap-2">{t6}</div>;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== page.description) {
    t8 = page.description && <span className="text-right text-caption text-foreground-secondary">{page.description}</span>;
    $[9] = page.description;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t7 || $[12] !== t8) {
    t9 = <div className="flex w-full items-center">{t5}<span className="flex w-full flex-row items-center justify-between">{t7}{t8}</span></div>;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t1 || $[15] !== t2 || $[16] !== t4 || $[17] !== t9) {
    t10 = <div><ProgressBarLink href={t1} onClick={t2} className={t4}>{t9}</ProgressBarLink></div>;
    $[14] = t1;
    $[15] = t2;
    $[16] = t4;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  return t10;
}
function _temp(item, index, array) {
  return <div key={`${index}-${item.route || "no-route"}`} className="flex items-center"><span className={cx("text-left text-body text-foreground-secondary", index === array.length - 1 && "font-semibold text-foreground")}>{item.name}</span>{index < array.length - 1 && <HiChevronRight className="mx-1 h-3 w-3 text-foreground-muted" />}</div>;
}
export function PatientSearchResult(t0) {
  const $ = _c(11);
  const {
    patient,
    onSelect,
    isSelected,
    highlight
  } = t0;
  const t1 = `/patient/${patient.id}`;
  const t2 = isSelected && STYLES.selected;
  let t3;
  if ($[0] !== t2) {
    t3 = cx(STYLES.container, t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <FaUser className={cx(STYLES.icon, "mr-2 mt-1 h-3 w-3")} />;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== highlight || $[4] !== patient) {
    t5 = <div className="flex w-full items-start">{t4}<PatientDisplay patient={patient} showPhoneNumber={true} showLocation={true} showDateOfBirth={true} showPronouns={true} layout="horizontal" useLinks={false} className="w-full" highlight={highlight} /></div>;
    $[3] = highlight;
    $[4] = patient;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== onSelect || $[7] !== t1 || $[8] !== t3 || $[9] !== t5) {
    t6 = <ProgressBarLink href={t1} className={t3} onClick={onSelect}>{t5}</ProgressBarLink>;
    $[6] = onSelect;
    $[7] = t1;
    $[8] = t3;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
}
export function CatalogItemSearchResult(t0) {
  const $ = _c(14);
  const {
    item,
    onSelect,
    isSelected
  } = t0;
  const t1 = isSelected && STYLES.selected;
  let t2;
  if ($[0] !== t1) {
    t2 = cx(STYLES.container, t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== item) {
    t3 = <ItemDisplay item={item} />;
    $[2] = item;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== item.discountTemplates || $[5] !== item.type) {
    t4 = item.type === ItemType.PACKAGE && <PackageContents discountTemplates={item.discountTemplates} emptyLabel="No discounts" className="ml-0" />;
    $[4] = item.discountTemplates;
    $[5] = item.type;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = <div className="min-w-0 flex-1">{t3}{t4}</div>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== onSelect || $[11] !== t2 || $[12] !== t5) {
    t6 = <div onClick={onSelect} className={t2}>{t5}</div>;
    $[10] = onSelect;
    $[11] = t2;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}

/** A gift-card search hit tagged so keyboard navigation can dispatch it. */
export type GiftCardSearchEntry = GiftCardCodeSearchResult & {
  _kind: "gift-card";
};

/** Deep-link to the issued gift cards page with a specific card's drawer open. */
export function giftCardSearchUrl(id: string): string {
  return `/settings/gift-cards?giftCardId=${encodeURIComponent(id)}`;
}
export function GiftCardSearchResult(t0) {
  const $ = _c(21);
  const {
    giftCard,
    onSelect,
    isSelected
  } = t0;
  const t1 = isSelected && STYLES.selected;
  let t2;
  if ($[0] !== t1) {
    t2 = cx(STYLES.container, t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <RiGiftLine className={cx(STYLES.icon, "")} />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== giftCard.code) {
    t4 = <span className="font-mono font-semibold text-foreground">{giftCard.code}</span>;
    $[3] = giftCard.code;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== giftCard.purchasingPatientName) {
    t5 = giftCard.purchasingPatientName && <span className="truncate text-caption text-foreground-secondary">{giftCard.purchasingPatientName}</span>;
    $[5] = giftCard.purchasingPatientName;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t4 || $[8] !== t5) {
    t6 = <div className="flex min-w-0 flex-row items-center gap-2">{t4}{t5}</div>;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = giftCard.currentBalance / 100;
  let t8;
  if ($[10] !== t7) {
    t8 = t7.toFixed(2);
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t8) {
    t9 = <span className="shrink-0 text-right text-caption text-foreground-secondary">${t8}</span>;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t6 || $[15] !== t9) {
    t10 = <div className="flex w-full items-center">{t3}<span className="flex w-full flex-row items-center justify-between gap-4">{t6}{t9}</span></div>;
    $[14] = t6;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== onSelect || $[18] !== t10 || $[19] !== t2) {
    t11 = <div onClick={onSelect} className={t2}>{t10}</div>;
    $[17] = onSelect;
    $[18] = t10;
    $[19] = t2;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  return t11;
}
interface SearchResultsProps {
  search: string;
  items: {
    pages?: SiteMapItem[];
    recentPages?: SiteMapItem[];
    patients?: PatientSummary[];
    catalogItems?: CatalogSearchItem[];
    giftCards?: GiftCardSearchEntry[];
    tenants?: TenantSelect[];
    locations?: LocationDetail[];
    themeModes?: ThemeMode[];
    actions?: CommandAction[];
    settings?: CommandAction[];
  };
  onSelect: (url: string, resultIndex: number, resultType: SearchResultType) => void;
  onTenantSelect: (subDomain: string, resultIndex: number) => void;
  onLocationSelect: (locationId: string | null, resultIndex: number) => void;
  onThemeModeSelect?: (mode: ThemeMode, resultIndex: number) => void;
  onActionSelect?: (action: CommandAction, resultIndex: number) => void;
  onAskDecoda?: (query: string) => void;
  /** When set, render a "Create a patient" CTA after the patient matches (shown with up to three). */
  createPatientCta?: {
    /** What was typed: a name, phone number or email. */
    label: string;
    onSelect: (resultIndex: number) => void;
  };
  selectedRow?: number;
  totalRows?: number;
  scrollContainerRef?: React.RefObject<HTMLDivElement | null>;
  isLoading?: boolean;
}
function TenantSearchResult(t0) {
  const $ = _c(14);
  const {
    tenant,
    onSelect,
    isSelected
  } = t0;
  const t1 = isSelected && STYLES.selected;
  let t2;
  if ($[0] !== t1) {
    t2 = cx(STYLES.container, t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <RiBuilding2Line className={cx(STYLES.icon, "")} />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== tenant.name) {
    t4 = <div className="mr-4 flex flex-row items-center"><span className="font-semibold text-foreground">{tenant.name}</span></div>;
    $[3] = tenant.name;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== tenant.subDomain) {
    t5 = <span className="text-right text-caption text-foreground-secondary">{tenant.subDomain}</span>;
    $[5] = tenant.subDomain;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t4 || $[8] !== t5) {
    t6 = <div className="flex w-full items-center">{t3}<span className="flex w-full flex-row items-center justify-between">{t4}{t5}</span></div>;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== onSelect || $[11] !== t2 || $[12] !== t6) {
    t7 = <div onClick={onSelect} className={t2}>{t6}</div>;
    $[10] = onSelect;
    $[11] = t2;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}
function LocationSearchResult(t0) {
  const $ = _c(22);
  const {
    location,
    onSelect,
    isSelected
  } = t0;
  let t1;
  if ($[0] !== location.city || $[1] !== location.country || $[2] !== location.isVirtual || $[3] !== location.state || $[4] !== location.zipCode) {
    t1 = () => {
      if (location.isVirtual) {
        return "Virtual";
      }
      if (location.city && location.state) {
        return `${location.city}, ${location.state}`;
      } else {
        if (location.city) {
          return location.city;
        } else {
          if (location.state) {
            return location.state;
          } else {
            if (location.zipCode) {
              return location.zipCode;
            } else {
              if (location.country) {
                return location.country;
              }
            }
          }
        }
      }
      return "No address details";
    };
    $[0] = location.city;
    $[1] = location.country;
    $[2] = location.isVirtual;
    $[3] = location.state;
    $[4] = location.zipCode;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const getLocationDisplay = t1;
  const t2 = isSelected && STYLES.selected;
  let t3;
  if ($[6] !== t2) {
    t3 = cx(STYLES.container, t2);
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <RiMapPin2Line className={cx(STYLES.icon, "")} />;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== location.name) {
    t5 = <div className="mr-4 flex flex-row items-center"><span className="font-semibold text-foreground">{location.name}</span></div>;
    $[9] = location.name;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== getLocationDisplay) {
    t6 = getLocationDisplay();
    $[11] = getLocationDisplay;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t6) {
    t7 = <span className="text-right text-caption text-foreground-secondary">{t6}</span>;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t5 || $[16] !== t7) {
    t8 = <div className="flex w-full items-center">{t4}<span className="flex w-full flex-row items-center justify-between">{t5}{t7}</span></div>;
    $[15] = t5;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== onSelect || $[19] !== t3 || $[20] !== t8) {
    t9 = <div onClick={onSelect} className={t3}>{t8}</div>;
    $[18] = onSelect;
    $[19] = t3;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
}
function ThemeModeSearchResult(t0) {
  const $ = _c(17);
  const {
    mode,
    onSelect,
    isSelected
  } = t0;
  let t1;
  if ($[0] !== mode) {
    t1 = () => {
      switch (mode) {
        case "light":
          {
            return <IoSunny className={cx(STYLES.icon, "")} />;
          }
        case "dark":
          {
            return <IoMoon className={cx(STYLES.icon, "")} />;
          }
        case "system":
          {
            return <HiOutlineComputerDesktop className={cx(STYLES.icon, "")} />;
          }
      }
    };
    $[0] = mode;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const getIcon = t1;
  let t2;
  if ($[2] !== mode) {
    t2 = () => {
      switch (mode) {
        case "light":
          {
            return "Switch to Light Theme";
          }
        case "dark":
          {
            return "Switch to Dark Theme";
          }
        case "system":
          {
            return "System Preference";
          }
      }
    };
    $[2] = mode;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const getDisplayName = t2;
  const t3 = isSelected && STYLES.selected;
  let t4;
  if ($[4] !== t3) {
    t4 = cx(STYLES.container, t3);
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== getIcon) {
    t5 = getIcon();
    $[6] = getIcon;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = getDisplayName();
  let t7;
  if ($[8] !== t6) {
    t7 = <span className="flex w-full flex-row items-center justify-between"><div className="mr-4 flex flex-row items-center"><span className="font-semibold text-foreground">{t6}</span></div></span>;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t5 || $[11] !== t7) {
    t8 = <div className="flex w-full items-center">{t5}{t7}</div>;
    $[10] = t5;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== onSelect || $[14] !== t4 || $[15] !== t8) {
    t9 = <div onClick={onSelect} className={t4}>{t8}</div>;
    $[13] = onSelect;
    $[14] = t4;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  return t9;
}
function ActionSearchResult(t0) {
  const $ = _c(24);
  const {
    action,
    onSelect,
    isSelected
  } = t0;
  const t1 = isSelected && STYLES.selected;
  let t2;
  if ($[0] !== t1) {
    t2 = cx(STYLES.container, t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = action.isDestructive && "opacity-100";
  let t4;
  if ($[2] !== t3) {
    t4 = cx(STYLES.icon, t3);
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== action.icon || $[5] !== t4) {
    t5 = <span className={t4}>{action.icon}</span>;
    $[4] = action.icon;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = action.isDestructive ? "text-status-error-text" : "text-foreground";
  let t7;
  if ($[7] !== t6) {
    t7 = cx("font-semibold", t6);
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== action.name || $[10] !== t7) {
    t8 = <div className="mr-4 flex flex-row items-center"><span className={t7}>{action.name}</span></div>;
    $[9] = action.name;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== action.description) {
    t9 = action.description && <span className="text-right text-caption text-foreground-secondary">{action.description}</span>;
    $[12] = action.description;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t8 || $[15] !== t9) {
    t10 = <span className="flex w-full flex-row items-center justify-between">{t8}{t9}</span>;
    $[14] = t8;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10 || $[18] !== t5) {
    t11 = <div className="flex w-full items-center">{t5}{t10}</div>;
    $[17] = t10;
    $[18] = t5;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== onSelect || $[21] !== t11 || $[22] !== t2) {
    t12 = <div onClick={onSelect} className={t2}>{t11}</div>;
    $[20] = onSelect;
    $[21] = t11;
    $[22] = t2;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  return t12;
}
export function CreatePatientSearchResult(t0) {
  const $ = _c(10);
  const {
    label,
    onSelect,
    isSelected
  } = t0;
  const t1 = isSelected && STYLES.selected;
  let t2;
  if ($[0] !== t1) {
    t2 = cx(STYLES.container, t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <RiUserAddLine className={cx(STYLES.icon, "")} />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className="font-semibold text-foreground">Create a patient</span>;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== label) {
    t5 = <div className="flex w-full items-center">{t3}<span className="flex w-full flex-row items-center gap-1">{t4}<span className="text-foreground-secondary">“{label}”</span></span></div>;
    $[4] = label;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== onSelect || $[7] !== t2 || $[8] !== t5) {
    t6 = <div onClick={onSelect} className={t2}>{t5}</div>;
    $[6] = onSelect;
    $[7] = t2;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
}
export function AskDecodaSearchResult(t0) {
  const $ = _c(10);
  const {
    query,
    onSelect,
    isSelected
  } = t0;
  const t1 = isSelected && STYLES.selected;
  let t2;
  if ($[0] !== t1) {
    t2 = cx(STYLES.container, t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <AIBadge size={14} className={cx("mr-2 opacity-70")} />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className="font-semibold text-foreground">Ask Decoda</span>;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== query) {
    t5 = <div className="flex w-full items-center">{t3}<span className="flex w-full flex-row items-center justify-between"><div className="mr-4 flex flex-row items-center gap-1">{t4}<span className="text-foreground-secondary">“{query}”</span></div></span></div>;
    $[4] = query;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== onSelect || $[7] !== t2 || $[8] !== t5) {
    t6 = <div onClick={onSelect} className={t2}>{t5}</div>;
    $[6] = onSelect;
    $[7] = t2;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
}
export function SearchResults(t0) {
  const $ = _c(83);
  const {
    search,
    items,
    onSelect,
    onTenantSelect,
    onLocationSelect,
    onThemeModeSelect,
    onActionSelect,
    onAskDecoda,
    createPatientCta,
    selectedRow,
    totalRows,
    scrollContainerRef,
    isLoading
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const resultRefs = useRef(t1);
  let t2;
  let t3;
  if ($[1] !== scrollContainerRef || $[2] !== selectedRow || $[3] !== totalRows) {
    t2 = () => {
      if (selectedRow === undefined) {
        return;
      }
      const scrollContainer = scrollContainerRef?.current;
      if (!scrollContainer) {
        return;
      }
      if (selectedRow === 0) {
        scrollContainer.scrollTo({
          top: 0,
          behavior: "smooth"
        });
        return;
      }
      if (totalRows !== undefined && selectedRow === totalRows - 1) {
        scrollContainer.scrollTo({
          top: scrollContainer.scrollHeight,
          behavior: "smooth"
        });
        return;
      }
      if (resultRefs.current[selectedRow]) {
        resultRefs.current[selectedRow]?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "nearest"
        });
      }
    };
    t3 = [selectedRow, totalRows, scrollContainerRef];
    $[1] = scrollContainerRef;
    $[2] = selectedRow;
    $[3] = totalRows;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  const hasNoResults = search.length >= SEARCH_MIN_LENGTH && items.pages?.length === 0 && items.patients?.length === 0 && (items.catalogItems?.length ?? 0) === 0 && (items.giftCards?.length ?? 0) === 0 && items.tenants?.length === 0 && items.locations?.length === 0 && items.themeModes?.length === 0 && items.actions?.length === 0 && items.settings?.length === 0 && !createPatientCta;
  if (hasNoResults && !onAskDecoda) {
    const t4 = isLoading ? "Loading..." : "No results found";
    let t5;
    if ($[6] !== t4) {
      t5 = <div className="flex items-center justify-center py-4 text-body text-muted-foreground">{t4}</div>;
      $[6] = t4;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    return t5;
  }
  const actionsCount = items.actions?.length ?? 0;
  const pagesCount = items.pages?.length ?? 0;
  const recentPagesCount = items.recentPages?.length ?? 0;
  const settingsCount = items.settings?.length ?? 0;
  const locationsCount = items.locations?.length ?? 0;
  const themeModesCount = items.themeModes?.length ?? 0;
  const tenantsCount = items.tenants?.length ?? 0;
  const pagesOffset = 0 + actionsCount;
  const recentPagesOffset = pagesOffset + pagesCount;
  const settingsOffset = recentPagesOffset + recentPagesCount;
  const locationsOffset = settingsOffset + settingsCount;
  const themeModesOffset = locationsOffset + locationsCount;
  const tenantsOffset = themeModesOffset + themeModesCount;
  const patientsOffset = tenantsOffset + tenantsCount;
  const patientsCount = items.patients?.length ?? 0;
  const createPatientOffset = patientsOffset + patientsCount;
  const createPatientCount = createPatientCta ? 1 : 0;
  const catalogItemsOffset = createPatientOffset + createPatientCount;
  const catalogItemsCount = items.catalogItems?.length ?? 0;
  const giftCardsOffset = catalogItemsOffset + catalogItemsCount;
  let t4;
  if ($[8] !== items.actions || $[9] !== onActionSelect || $[10] !== selectedRow) {
    t4 = items.actions && items.actions.length > 0 && onActionSelect && <div><div className={STYLES.heading}>Actions</div>{items.actions.map((action, index) => {
        const refIndex = 0 + index;
        return <div key={`action-${action.id}`} ref={el => {
          resultRefs.current[refIndex] = el;
        }}><ActionSearchResult action={action} onSelect={() => onActionSelect(action, refIndex)} isSelected={selectedRow === refIndex} /></div>;
      })}</div>;
    $[8] = items.actions;
    $[9] = onActionSelect;
    $[10] = selectedRow;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== items.pages || $[13] !== onSelect || $[14] !== pagesOffset || $[15] !== search.length || $[16] !== selectedRow) {
    t5 = items.pages && items.pages.length > 0 && <div><div className={STYLES.heading}>{search.length >= SEARCH_MIN_LENGTH ? "Pages" : "Suggested Pages"}</div>{items.pages.map((page, index_0) => {
        const refIndex_0 = pagesOffset + index_0;
        const resultType = search.length >= SEARCH_MIN_LENGTH ? "page" : "suggested_page";
        return <div key={`page-${page.route ?? index_0}`} ref={el_0 => {
          resultRefs.current[refIndex_0] = el_0;
        }}><PageSearchResult page={page} onSelect={() => page.route && onSelect(page.route, refIndex_0, resultType)} isSelected={selectedRow === refIndex_0} /></div>;
      })}</div>;
    $[12] = items.pages;
    $[13] = onSelect;
    $[14] = pagesOffset;
    $[15] = search.length;
    $[16] = selectedRow;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== items.recentPages || $[19] !== onSelect || $[20] !== recentPagesOffset || $[21] !== selectedRow) {
    t6 = items.recentPages && items.recentPages.length > 0 && <div><div className={STYLES.heading}>Recent Pages</div>{items.recentPages.map((page_0, index_1) => {
        const refIndex_1 = recentPagesOffset + index_1;
        return <div key={`recent-page-${page_0.route ?? index_1}`} ref={el_1 => {
          resultRefs.current[refIndex_1] = el_1;
        }}><PageSearchResult page={page_0} onSelect={() => page_0.route && onSelect(page_0.route, refIndex_1, "recent_page")} isSelected={selectedRow === refIndex_1} /></div>;
      })}</div>;
    $[18] = items.recentPages;
    $[19] = onSelect;
    $[20] = recentPagesOffset;
    $[21] = selectedRow;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== items.settings || $[24] !== onActionSelect || $[25] !== selectedRow || $[26] !== settingsOffset) {
    t7 = items.settings && items.settings.length > 0 && onActionSelect && <div><div className={STYLES.heading}>Settings</div>{items.settings.map((setting, index_2) => {
        const refIndex_2 = settingsOffset + index_2;
        return <div key={`setting-${setting.id}`} ref={el_2 => {
          resultRefs.current[refIndex_2] = el_2;
        }}><ActionSearchResult action={setting} onSelect={() => onActionSelect(setting, refIndex_2)} isSelected={selectedRow === refIndex_2} /></div>;
      })}</div>;
    $[23] = items.settings;
    $[24] = onActionSelect;
    $[25] = selectedRow;
    $[26] = settingsOffset;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let t8;
  if ($[28] !== items.locations || $[29] !== locationsOffset || $[30] !== onLocationSelect || $[31] !== selectedRow) {
    t8 = items.locations && items.locations.length > 0 && <div><div className={STYLES.heading}>Locations</div>{items.locations.map((location, index_3) => {
        const refIndex_3 = locationsOffset + index_3;
        return <div key={`location-${location.id}`} ref={el_3 => {
          resultRefs.current[refIndex_3] = el_3;
        }}><LocationSearchResult location={location} onSelect={() => onLocationSelect(location.id, refIndex_3)} isSelected={selectedRow === refIndex_3} /></div>;
      })}</div>;
    $[28] = items.locations;
    $[29] = locationsOffset;
    $[30] = onLocationSelect;
    $[31] = selectedRow;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  let t9;
  if ($[33] !== items.themeModes || $[34] !== onThemeModeSelect || $[35] !== selectedRow || $[36] !== themeModesOffset) {
    t9 = items.themeModes && items.themeModes.length > 0 && onThemeModeSelect && <div><div className={STYLES.heading}>Theme</div>{items.themeModes.map((mode, index_4) => {
        const refIndex_4 = themeModesOffset + index_4;
        return <div key={`theme-mode-${mode}`} ref={el_4 => {
          resultRefs.current[refIndex_4] = el_4;
        }}><ThemeModeSearchResult mode={mode} onSelect={() => onThemeModeSelect(mode, refIndex_4)} isSelected={selectedRow === refIndex_4} /></div>;
      })}</div>;
    $[33] = items.themeModes;
    $[34] = onThemeModeSelect;
    $[35] = selectedRow;
    $[36] = themeModesOffset;
    $[37] = t9;
  } else {
    t9 = $[37];
  }
  let t10;
  if ($[38] !== items.tenants || $[39] !== onTenantSelect || $[40] !== selectedRow || $[41] !== tenantsOffset) {
    t10 = items.tenants && items.tenants.length > 0 && <div><div className={STYLES.heading}>Tenants</div>{items.tenants.map((tenantItem, index_5) => {
        const refIndex_5 = tenantsOffset + index_5;
        return <div key={`tenant-${tenantItem.subDomain}`} ref={el_5 => {
          resultRefs.current[refIndex_5] = el_5;
        }}><TenantSearchResult tenant={tenantItem} onSelect={() => onTenantSelect(tenantItem.subDomain, refIndex_5)} isSelected={selectedRow === refIndex_5} /></div>;
      })}</div>;
    $[38] = items.tenants;
    $[39] = onTenantSelect;
    $[40] = selectedRow;
    $[41] = tenantsOffset;
    $[42] = t10;
  } else {
    t10 = $[42];
  }
  let t11;
  if ($[43] !== items.patients || $[44] !== onSelect || $[45] !== patientsOffset || $[46] !== search || $[47] !== selectedRow) {
    t11 = items.patients && items.patients.length > 0 && <div><div className={STYLES.heading}>{search.length >= SEARCH_MIN_LENGTH ? "Patients" : "Recent Patients"}</div>{items.patients.map((patient, index_6) => {
        const refIndex_6 = patientsOffset + index_6;
        const resultType_0 = search.length >= SEARCH_MIN_LENGTH ? "patient" : "recent_patient";
        return <div key={`patient-${patient.id}`} ref={el_6 => {
          resultRefs.current[refIndex_6] = el_6;
        }}><PatientSearchResult patient={patient} onSelect={() => {
            onSelect(`/patient/${patient.id}`, refIndex_6, resultType_0);
          }} isSelected={selectedRow === refIndex_6} highlight={resultType_0 === "patient" ? search : undefined} /></div>;
      })}</div>;
    $[43] = items.patients;
    $[44] = onSelect;
    $[45] = patientsOffset;
    $[46] = search;
    $[47] = selectedRow;
    $[48] = t11;
  } else {
    t11 = $[48];
  }
  let t12;
  if ($[49] !== createPatientCta || $[50] !== createPatientOffset || $[51] !== patientsCount || $[52] !== selectedRow) {
    t12 = createPatientCta && <div>{patientsCount === 0 && <div className={STYLES.heading}>Patients</div>}<div ref={el_7 => {
        resultRefs.current[createPatientOffset] = el_7;
      }}><CreatePatientSearchResult label={createPatientCta.label} onSelect={() => createPatientCta.onSelect(createPatientOffset)} isSelected={selectedRow === createPatientOffset} /></div></div>;
    $[49] = createPatientCta;
    $[50] = createPatientOffset;
    $[51] = patientsCount;
    $[52] = selectedRow;
    $[53] = t12;
  } else {
    t12 = $[53];
  }
  let t13;
  if ($[54] !== catalogItemsOffset || $[55] !== items.catalogItems || $[56] !== onSelect || $[57] !== selectedRow) {
    t13 = items.catalogItems && items.catalogItems.length > 0 && <div><div className={STYLES.heading}>Catalog Items</div>{items.catalogItems.map((item, index_7) => {
        const refIndex_7 = catalogItemsOffset + index_7;
        const resultType_1 = item.type.toLowerCase() as SearchResultType;
        const url = `/settings/${item.type.toLowerCase()}s?id=${encodeURIComponent(item.id)}`;
        return <div key={`catalog-${item.id}`} ref={el_8 => {
          resultRefs.current[refIndex_7] = el_8;
        }}><CatalogItemSearchResult item={item} onSelect={() => onSelect(url, refIndex_7, resultType_1)} isSelected={selectedRow === refIndex_7} /></div>;
      })}</div>;
    $[54] = catalogItemsOffset;
    $[55] = items.catalogItems;
    $[56] = onSelect;
    $[57] = selectedRow;
    $[58] = t13;
  } else {
    t13 = $[58];
  }
  let t14;
  if ($[59] !== giftCardsOffset || $[60] !== items.giftCards || $[61] !== onSelect || $[62] !== search.length || $[63] !== selectedRow) {
    t14 = search.length >= SEARCH_MIN_LENGTH && items.giftCards && items.giftCards.length > 0 && <div><div className={STYLES.heading}>Gift Cards</div>{items.giftCards.map((giftCard, index_8) => {
        const refIndex_8 = giftCardsOffset + index_8;
        return <div key={`gift-card-${giftCard.id}`} ref={el_9 => {
          resultRefs.current[refIndex_8] = el_9;
        }}><GiftCardSearchResult giftCard={giftCard} onSelect={() => onSelect(giftCardSearchUrl(giftCard.id), refIndex_8, "gift_card")} isSelected={selectedRow === refIndex_8} /></div>;
      })}</div>;
    $[59] = giftCardsOffset;
    $[60] = items.giftCards;
    $[61] = onSelect;
    $[62] = search.length;
    $[63] = selectedRow;
    $[64] = t14;
  } else {
    t14 = $[64];
  }
  let t15;
  if ($[65] !== onAskDecoda || $[66] !== search || $[67] !== selectedRow || $[68] !== totalRows) {
    t15 = search.length >= SEARCH_MIN_LENGTH && onAskDecoda && <div><AskDecodaSearchResult query={search} onSelect={() => onAskDecoda(search)} isSelected={selectedRow === (totalRows ?? 0) - 1} /></div>;
    $[65] = onAskDecoda;
    $[66] = search;
    $[67] = selectedRow;
    $[68] = totalRows;
    $[69] = t15;
  } else {
    t15 = $[69];
  }
  let t16;
  if ($[70] !== t10 || $[71] !== t11 || $[72] !== t12 || $[73] !== t13 || $[74] !== t14 || $[75] !== t15 || $[76] !== t4 || $[77] !== t5 || $[78] !== t6 || $[79] !== t7 || $[80] !== t8 || $[81] !== t9) {
    t16 = <div className="space-y-4">{t4}{t5}{t6}{t7}{t8}{t9}{t10}{t11}{t12}{t13}{t14}{t15}</div>;
    $[70] = t10;
    $[71] = t11;
    $[72] = t12;
    $[73] = t13;
    $[74] = t14;
    $[75] = t15;
    $[76] = t4;
    $[77] = t5;
    $[78] = t6;
    $[79] = t7;
    $[80] = t8;
    $[81] = t9;
    $[82] = t16;
  } else {
    t16 = $[82];
  }
  return t16;
}